import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";
import CloudUploadRounded from "@mui/icons-material/CloudUploadRounded";
import Resizer from "react-image-file-resizer";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { db, storage } from "services/firebase";
import { updateInstitutionLogo } from "services/fde/championVideoRolloutService";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const inlineLoading = (
  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
    <CircularProgress />
  </Box>
);

function resizeLogo(file) {
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      500,
      200,
      file.type === "image/png" ? "PNG" : "JPEG",
      90,
      0,
      (uri) => resolve(uri),
      "file",
    );
  });
}

const InstitutionBrandPage = () => {
  const { user, userData, organizationId, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId || userData?.institutionId || organizationId || null;

  const profileReady = userData !== undefined;
  const canAccess = isGlobalAdmin || userData?.role === "admin";

  const [orgName, setOrgName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadOrg = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const snap = await db.collection("organizations").doc(institutionId).get();
      if (!snap.exists) {
        setOrgName("");
        setLogoUrl("");
        setLoadError(true);
        setErrorMessage("Institution not found.");
      } else {
        const data = snap.data() || {};
        setOrgName(data.name || "");
        setLogoUrl(data.logoUrl || "");
        setImagePreview("");
        setPendingFile(null);
      }
    } catch (err) {
      setLoadError(true);
      setErrorMessage(err?.message || "Failed to load institution branding.");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    if (!profileReady || !canAccess || !institutionId) {
      setLoading(false);
      return undefined;
    }
    loadOrg();
    return undefined;
  }, [profileReady, canAccess, institutionId, loadOrg]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setErrorMessage("The image must be a .jpg or .png file.");
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const resized = await resizeLogo(file);
      setPendingFile(resized);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(String(reader.result || ""));
      reader.readAsDataURL(resized);
    } catch (err) {
      setErrorMessage(err?.message || "Could not process image.");
    }
  };

  const handleSave = async () => {
    if (!pendingFile || !institutionId || !user?.uid) return;
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const fileExtension = pendingFile.type === "image/png" ? ".png" : ".jpg";
      const filePath = `assets/images/institution-logos/${institutionId}${fileExtension}`;
      const snapshot = await storage.ref().child(filePath).put(pendingFile);
      const downloadUrl = await snapshot.ref.getDownloadURL();
      await updateInstitutionLogo({ institutionId, logoUrl: downloadUrl });
      setLogoUrl(downloadUrl);
      setPendingFile(null);
      setImagePreview("");
      setSuccessMessage("Institution logo saved.");
    } catch (err) {
      setErrorMessage(
        err?.message || err?.details || "Failed to save institution logo.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (!profileReady) return inlineLoading;

  if (!canAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Institution admin access is required.</Alert>
      </Box>
    );
  }

  if (!institutionId) {
    return (
      <Stack spacing={3}>
        <DashboardPageHeader
          title="Customize & Brand"
          subtitle="Logo and institution branding"
        />
        <Alert severity="info">
          No institution is linked to your account yet.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <DashboardPageHeader
        title="Customize & Brand"
        subtitle="Logo and institution branding for champion rollout videos"
      />

      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      {loadError && !errorMessage && (
        <Alert severity="error">Could not load institution branding.</Alert>
      )}

      {loading ? (
        inlineLoading
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <Typography variant="subtitle1">{orgName || institutionId}</Typography>
          <Typography variant="body2" color="text.secondary">
            Institution name is managed in Institution setup. Upload a logo here
            for champion rollout videos.
          </Typography>

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
              bgcolor: "background.paper",
            }}
          >
            {(imagePreview || logoUrl) ? (
              <Box
                component="img"
                src={imagePreview || logoUrl}
                alt="Institution logo"
                sx={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain" }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No logo uploaded yet
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadRounded />}
            >
              {logoUrl || imagePreview ? "Change logo" : "Upload logo"}
              <VisuallyHiddenInput
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleFile}
              />
            </Button>
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={!pendingFile}
              onClick={handleSave}
            >
              Save logo
            </LoadingButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default InstitutionBrandPage;
